import { makeAutoObservable, action } from "mobx";
import _ from "lodash";
import repository from "src/repositories/Document";
import searchRepository from "src/repositories/Search";
import indexRepository from "src/repositories/Indices";
import {
  DisplayItem,
  Dict,
  DSLItem,
  Entrance,
  SearchMethodType,
  SelectType,
  StoreState,
  IndexJob,
} from "src/types/common";
import { DISPLAY_ITEMS, SORT } from "src/constants";
import { Index } from "src/types/index/model";
import { IndexFieldResponse } from "src/types/index/schema";
import { PythonDSL } from "src/types/python-dsl/model";
import { Demo } from "src/types/demo/model";
import {
  SingleDocumentCreate,
  SingleDocumentUpdate,
} from "src/types/document/schema";
import {
  RangeFilter,
  RankingRule,
  SearchCriteria,
  SearchResponse,
  SearchRule,
  SortDictType,
  TermFilter,
} from "src/types/search/schema";

export default class DocumentList {
  state: StoreState = "none";
  dslState: StoreState = "none";
  job?: IndexJob;
  entrance: Entrance = "browse";
  searchMethod: SearchMethodType = "gui";
  subTab: "searchRule" | "scoreRanking" | "highlighting" = "searchRule";
  result?: SearchResponse;
  fields?: IndexFieldResponse;
  dslResult?: SearchResponse;
  sortingOptions?: SelectType[];
  displayItems: DisplayItem[] = [];
  originDisplayItems: DisplayItem[] = [];
  query: string = "";
  dslQuery: DSLItem[] = [
    { key: "", value: "" },
    { key: "", value: "" },
  ];
  page: number = 1;
  size: number = 10;
  range_filters: RangeFilter[] = [];
  terms_filters: TermFilter[] = [];
  search_rules: SearchRule[] = [];
  ranking_rules: RankingRule[] = [];
  sort_rules: SortDictType[] = [];
  use_bannedword?: boolean;

  // have not yet considered

  field_value_factor?: Dict;
  date_decay_weight?: Dict;
  geo_decay_weight?: Dict;
  numeric_decay_weight?: Dict;
  script_score?: Dict;

  highlight_options?: Dict;

  constructor() {
    makeAutoObservable(this);
  }

  setSearchMethod = action(async (method: SearchMethodType) => {
    this.searchMethod = method;
  });

  setSubTab = action((subTab: "searchRule" | "scoreRanking" | "highlighting") => {
    this.subTab = subTab;
  });

  setQuery = action(async (query?: string) => {
    if (!query) query = "";
    this.query = query;
  });

  setPage = action(async (page?: number) => {
    if (!page) page = 1;
    this.page = page;
  });

  setSize = action(async (size?: number) => {
    if (!size) size = 10;
    this.size = size;
  });

  setSortOptions = action(async (option: SortDictType) => {
    this.sort_rules = [option];
  });

  setDSLQuerys = action(async (dslItems: DSLItem[]) => {
    this.dslQuery = dslItems;
  });

  setDSLQuery = action(async (index: number, key?: string, value?: string) => {
    if (key) this.dslQuery[index].key = key;
    if (value) this.dslQuery[index].value = value;
  });

  setMultiSortOptions = action(async (option: SortDictType) => {
    // 다중 소팅 시
    if (this.sort_rules.some((r) => r.field === option.field))
      this.sort_rules = this.sort_rules.filter((r) => r !== option);
    else this.sort_rules.push(option);
  });

  setDisplayItems = action(async (displayFields: string[]) => {
    if (!this.result!.hits) {
      return;
    }
    
    if (displayFields.length === 0) {
      displayFields = this.fields!.parent_fields.map((f) => f.name!);
    }
    
    this.displayItems = DISPLAY_ITEMS(
      displayFields,
      this.fields!.parent_fields
    );
    this.originDisplayItems = _.cloneDeep(this.displayItems);
  });

  setTermFilters = action(async (field: string, terms: string[]) => {
    if (this.terms_filters.some((f) => f.field === field))
      this.terms_filters = this.terms_filters.filter((f) => f.field !== field);
    if (terms.length === 0) return;
    this.terms_filters.push({ field: field, terms: terms });
  });

  setRangeFilters = action(async (
    field: string,
    gte: string | number,
    lte: string | number
  ) => {
    if (this.range_filters.some((f) => f.field === field))
      this.range_filters = this.range_filters.filter((f) => f.field !== field);
    this.range_filters.push({ field: field, gte: gte, lte: lte });
  });

  popRangeFilter = action(async (field: string) => {
    this.range_filters = this.range_filters.filter((f) => f.field !== field);
  });

  getSortingOption() {
    if (this.sort_rules.length <= 0) return;

    return this.sortingOptions!.filter(
      (o) => o.value.field === this.sort_rules[0].field).filter((o) => o.value.order === this.sort_rules[0].order
      )[0];
  }

  popDisplayItem = action(async (index: number) => {
    return this.displayItems.splice(index, 1);
  });

  pushDisplayItem = action(async (index: number, item: DisplayItem) => {
    this.displayItems.splice(index, 0, item);
  });

  turnDisplayOnOff = action(async (index: number) => {
    this.displayItems[index].display = !this.displayItems[index].display;
    // clone 하지 않으면 리랜더링이 안 일어남
    this.displayItems = _.clone(this.displayItems);
  });

  displayHit(hit: Dict) {
    const result: Dict = {};
    this.originDisplayItems.forEach((item) => {
      if (hit[item.value] !== "" && !_.isUndefined(hit[item.value]))
        result[item.value] = hit[item.value];
    });
    return result;
  }

  filterHit(hit: Dict) {
    const result: Dict = {};
    this.originDisplayItems.forEach((item) => {
      if (item.display && !_.isUndefined(hit[item.value])) result[item.value] = hit[item.value];
    });
    return result;
  }

  resetDisplay = action(async () => {
    this.displayItems = _.cloneDeep(this.originDisplayItems);
  });

  async getDisplayFields() {
    return this.displayItems.filter((i) => i.display).map((i) => i.value);
  }

  updateSortFields = action(async (filters: string[]) => {
    if (!filters.length) {
      this.terms_filters = [];
      this.range_filters = [];
      return;
    }
    this.terms_filters = this.terms_filters.filter((f) =>
      filters.includes(f.field)
    );
    this.range_filters = this.range_filters.filter((f) =>
      filters.includes(f.field)
    );
  });

  updateFilterFields = action(async (sorts: string[]) => {
    if (!sorts.length) {
      this.sort_rules = [];
      return;
    }
    this.sort_rules = this.sort_rules.filter((r) => sorts.includes(r.field));
  });

  initializeSortingOptions = action(async (index: Index) => {
    this.sortingOptions = SORT(
      this.fields!.aggregatable_fields.filter((f) =>
        index.sorting_fields!.some((s) => s === f.name)
      )
    );
  });

  initializeDemoSortingOptions = action(async (demoPayload: Demo) => {
    if (demoPayload.sorting_fields) {
      if (!demoPayload.sorting_fields) this.sortingOptions = [];
      this.sortingOptions = SORT(
        this.fields!.aggregatable_fields.filter((f) =>
          demoPayload.sorting_fields!.some((s) => s === f.name)
        )
      );
    }
  });

  async getDemoFields(demoPayload: Demo) {
    const fields: string[] = [];
    if (demoPayload.content_fields)
      demoPayload.content_fields.forEach((f) => fields.push(f));
    if (demoPayload.title_field) fields.push(demoPayload.title_field);
    return fields ? fields : undefined;
  }

  async getPayload(demoPayload?: Demo) {
    return {
      query: this.query,
      page: this.page,
      size: this.size,
      // sorting 옵션이 있을 때만 sort_rules 포함
      ...(this.sort_rules.length > 0 && { sort_rules: this.sort_rules }),
      include: demoPayload ? await this.getDemoFields(demoPayload) : undefined,
      range_filters: this.range_filters,
      terms_filters: this.terms_filters,
      search_rules: this.search_rules,
      ranking_rules: this.ranking_rules,
      use_bannedword: this.use_bannedword,
      highlight_options: this.highlight_options
    };
  }

  readMany = action(async (
    applicationId: string,
    index: Index,
    entrance: Entrance,
    pythonDSL?: PythonDSL,
    demoPayload?: Demo,
    payload?: SearchCriteria
  ) => {
    this.state = "pending";
    this.entrance = entrance;
    try {
      await this.readFields(applicationId, index.name);
      if (entrance === "browse") {
        // 인덱스 변경 시 기존 sort_rules 초기화
        this.sort_rules = [];
        await this.initializeSortingOptions(index);
        // 새로운 인덱스에 sorting 옵션이 있을 때만 기본값 설정
        if (this.sortingOptions?.length)
          await this.setSortOptions(this.sortingOptions[0].value);
      }
      if (entrance === "demo") {
        // 인덱스 변경 시 기존 sort_rules 초기화
        this.sort_rules = [];
        await this.initializeDemoSortingOptions(demoPayload!);
        // 새로운 인덱스에 sorting 옵션이 있을 때만 기본값 설정
        if (this.sortingOptions?.length)
          await this.setSortOptions(this.sortingOptions[0].value);
      }
      if (entrance.includes("search-setting")) {
        this.search_rules = index.search_rules!;
        this.ranking_rules = index.ranking_rules!;
        this.use_bannedword = index.use_bannedword!;
        this.highlight_options = index.highlight_options!;
      }
      if (entrance === "search-setting-dsl") {
        await this.readDSLMany(applicationId, index, pythonDSL!);
      } else {
        if (!payload)
          payload = await this.getPayload(
            demoPayload ? demoPayload : undefined
          );
        const response = await searchRepository.read(
          applicationId,
          index.name,
          payload
        );
        this.result = response.data;
      }
      this.state = "done";
    } catch (e) {
      this.state = "error";
      throw e;
    }
  });

  readFields = action(async (applicationId: string, indexName: string) => {
    try {
      const response = await indexRepository.readFields(
        applicationId,
        indexName
      );
      this.fields = response.data;
    } catch (e) {
      throw e;
    }
  });

  async getDSLFunctionParams() {
    const functionParams: Dict = {};
    this.dslQuery.forEach((query) => {
      if (query.key) functionParams[query.key] = query.value;
    });
    return functionParams;
  }

  readDSLMany = action(async (applicationId: string, index: Index, pythonDSL: PythonDSL) => {
    this.dslState = "pending";
    this.state = "pending";
    try {
      const functionParams = await this.getDSLFunctionParams();
      const response = await searchRepository.readDSL(
        applicationId,
        index.name,
        pythonDSL._id,
        {
          page: this.page,
          size: this.size,
          function_params: functionParams,
          python_dsl: pythonDSL.dsl_function,
        }
      );
      this.dslResult = response.data;
      this.dslState = "done";
      this.state = "done";
    } catch (e) {
      this.dslState = "error";
      this.state = "error";
      throw e;
    }
  });

  create = action(async (
    applicationId: string,
    indexName: string,
    payload: SingleDocumentCreate
  ) => {
    this.job = "createDocument";
    try {
      await repository.create(applicationId, indexName, payload);
      this.job = undefined;
    } catch (e) {
      this.job = undefined;
      throw e;
    }
  });

  async read(applicationId: string, indexName: string, documentId: string) {
    try {
      const response = await repository.read(
        applicationId,
        indexName,
        documentId
      );
      return response.data;
    } catch (e) {
      throw e;
    }
  }

  delete = action(async (applicationId: string, indexName: string, documentId: string) => {
    this.job = "deleteDocument";
    try {
      await repository.delete(applicationId, indexName, documentId);
      await this.popDocument(documentId);
      this.job = undefined;
    } catch (e) {
      this.job = undefined;
      throw e;
    }
  });

  popDocument = action(async (documentId: string) => {
    if (this.result) {
      this.result!.hits = this.result!.hits.filter((h) => h._id !== documentId);
      this.result!.total = this.result!.total - 1;
    }
  });

  update = action(async (
    applicationId: string,
    indexName: string,
    documentId: string,
    payload: SingleDocumentUpdate
  ) => {
    this.job = "updateDocument";
    try {
      await repository.update(applicationId, indexName, documentId, payload);
      this.job = undefined;
    } catch (e) {
      this.job = undefined;
      throw e;
    }
  });

  reindex = action(async (applicationId: string, indexName: string) => {
    this.job = "reindex";
    try {
      await repository.reindex(applicationId, indexName);
      this.job = undefined;
    } catch (e) {
      this.job = undefined;
      throw e;
    }
  });

  upload = action(async (applicationId: string, indexName: string, payload: FormData) => {
    this.job = "uploadDocuments";
    try {
      await repository.upload(applicationId, indexName, payload);
      this.job = undefined;
    } catch (e) {
      this.job = undefined;
      throw e;
    }
  });

  clearDSL = action(async () => {
    this.dslState = "none";
    this.dslQuery = [
      { key: "", value: "" },
      { key: "", value: "" },
    ];
    this.dslResult = undefined;
    this.page = 1;
    this.size = 10;
  });

  clearGUI = action(async () => {
    this.result = undefined;
    this.query = "";
    this.page = 1;
    this.size = 10;
    this.search_rules = [];
    this.ranking_rules = [];
  });

  clear = action(async () => {
    this.state = "none";
    this.dslState = "none";
    this.job = undefined;
    this.entrance = "browse";
    this.searchMethod = "gui";
    this.subTab = "searchRule";
    this.result = undefined;
    this.fields = undefined;
    this.sortingOptions = [];
    this.displayItems = [];
    this.originDisplayItems = [];
    this.query = "";
    this.dslQuery = [
      { key: "", value: "" },
      { key: "", value: "" },
    ];
    this.page = 1;
    this.size = 10;
    this.range_filters = [];
    this.terms_filters = [];
    this.search_rules = [];
    this.ranking_rules = [];
    this.sort_rules = [];
  });

  setReady = action(async () => {
    this.state = "none";
  });

  // sorting 변경 시에만 사용하는 검색 함수 (readFields 재호출 안함)
  searchWithCurrentFields = action(async (applicationId: string, indexName: string, payload?: SearchCriteria) => {
    this.state = "pending";
    try {
      if (!payload) payload = await this.getPayload();
      const response = await searchRepository.read(applicationId, indexName, payload);
      this.result = response.data;
      this.state = "done";
    } catch (e) {
      this.state = "error";
      throw e;
    }
  });
}
